import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { PAGE_SIZE_MAX, PROJECT_STATUS, tutorialConstants } from '../../../utils/constants';
import { SUPER_ADMIN_ID } from '../../../utils/ids';
import { setEndDate, setFilters, setInspectionType, setLimit, setProjectStatus, setStartDate } from '../../../store/projectsQuery';

import Button from '../../themed/button';
import CreateProject from '../../forms/createProject';
import CreateNewInspectionType from '../../forms/createNewInspectionType';
import Confirm from '../../themed/confirm';
import ClearReportCacheButton from '../../blocks/clear-report-cache-button';
import Conditional from '../../common/conditional';
import DatePickerInput from '../../themed/date-picker';
import Elevation from '../../themed/instruction-modals/elevation';
import Grid from '../../blocks/grid';
import Header from '../../blocks/header';
import Filters from '../../blocks/filters';
import ItemCount from '../../blocks/item-count';
import Instruction from '../../themed/instruction-modals/instruction';
import Modal from '../../themed/modal';
import NoItems from '../../blocks/no-items/';
import ProjectStatusButton from '../../themed/project-status-button';
import ReportOverview from '../../blocks/report-overview';
import Select from '../../themed/select/select';
import SyncTime from '../../blocks/sync-time';
import Table from '../../common/table';

import withInspectionTypes from '../../hoc/withInspectionTypes';
import useInternalDashboard from '../../../hooks/useInternalDashboard';
import withProjects, { ProjectProps } from '../../hoc/withProjects';

import css from './projects.module.scss';
import routes from '../../../utils/routes';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';
import * as auth from '../../../services/authService';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

interface Props extends ProjectProps {
	types: models.SelectOption[];	
}

const Projects: React.FC<Props> = ({ onArchiveProject, dataLength, projects, hasNextPage, fetchNextPage, pages, totalProjects, projectsLastUpdated, projectsLoading, refreshProjects, types }) => {
	const [sortColumn, setSortColumn] = useState(initialSortColumn);
	const [createProject, setCreateProject] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const [projectToArchive, setProjectToArchive] = useState<models.Project | null>(null);
	const [privileges, setPrivileges] = useState<models.TutorialInstruction[]>([]);
	const [isOverviewVisible, setOverviewVisible] = useState(false);
	const [isNewInspectionVisible, setNewInspectionVisible] = useState(false);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	
	const internalDashboard = useInternalDashboard();
	const tutorials = useAppSelector(getTutorials);
	const projectsQuery = useAppSelector((state) => state.projectsQuery);

	const isActive = tutorials.activeTutorial === tutorialConstants.ALL_PROJECTS;
	const user = auth.getCurrentUser() as models.User;

	const tableColumns = useMemo(() => {
		let allColumns = [...columns];

		if (config.archiveProjects && user.role.approveReports) {
			const archiveReportColumn: models.Column = {
				path: 'inspectionTypeId',
				label: 'Archive',
				content: (item: models.Project) => (
					<button
						className={css['projects-archive-button']}
						onClick={() => setProjectToArchive(item)}
					>
						<i className="bi bi-archive-fill"></i>
					</button>
				),
			};

			allColumns = allColumns.concat([archiveReportColumn]);
		}

		if (user.id === SUPER_ADMIN_ID) {
			const clearReportCacheColumn: models.Column = {
				path: '_id',
				label: 'Cache',
				content: (item: models.Project) => (
					<ClearReportCacheButton projectId={item._id} />
				),
			};

			allColumns = allColumns.concat([clearReportCacheColumn]);
		}

		return allColumns;
	}, [user.id, user.role.approveReports]);

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = +e.target.value;
		dispatch(setLimit(pageSize));
	};

	const handleInspectionType = (e: React.ChangeEvent<HTMLSelectElement>) => {
		dispatch(setInspectionType(e.target.value));
	};

	const handleViewNextTutorial = () => {
		const nextTutorial: number = activeIndex + 1;
		if (nextTutorial > privileges.length - 1) {
			if (user.role.manageReports) {
				dispatch(setActiveTutorial(tutorialConstants.ALL_PROJECTS));
				navigate(routes.projects.fullPath);
			} else {
				dispatch(setActiveTutorial(tutorialConstants.PROJECT_DETAILS));
			}
		} else {
			setActiveIndex(nextTutorial);
			dispatch(setActiveTutorial(privileges[nextTutorial].id));
		}
	};

	const handleClearDateSelections = () => {
		dispatch(setStartDate(undefined));
		dispatch(setEndDate(undefined));
	};

	const handleCloseProjectToArchive = () => {
		setProjectToArchive(null);
	};

	const handleArchiveProject = async () => {
		if (!projectToArchive) {
			return toastService.error('Please select a project to archive');
		}

		handleCloseProjectToArchive();
		onArchiveProject(projectToArchive._id);
	};

	const handleFilterChange = (newFilters: models.FilterItem[]) => {
		dispatch(setFilters(newFilters));
		refreshProjects();
	};

	const renderStatusButtons = () => {
		const projectStatuses = [["All Projects", undefined]].concat(Object.entries(PROJECT_STATUS));

		return (
			<>
				{projectStatuses.map(([label, status]) => (
					<ProjectStatusButton
						key={label}
						status={status ?? label!}
						isActive={projectsQuery.projectStatus ? projectsQuery.projectStatus.includes(status!) : false}
						onClick={() => dispatch(setProjectStatus(status!))}
					/>
				))}
			</>
		);
	};

	const toggleCreateProject = (): void => {
		setCreateProject((prev: boolean) => !prev);
	};

	const toggleOverviewVisible = (): void => {
		setOverviewVisible((prev: boolean) => !prev);
	};
	
	const toggleInspectionVisible = (): void => {
		setNewInspectionVisible((prev: boolean) => !prev);
	};

	useEffect(() => {
		const initializePrivileges = () => {
			const newPrivileges: models.TutorialInstruction[] = [];
			if (user.role.manageProjects) {
				newPrivileges.push(allPrivileges.CREATE_PROJECT);
			}

			newPrivileges.push(allPrivileges.ALL_PROJECTS);
			setPrivileges(newPrivileges);
		};

		initializePrivileges();
	}, [user.role.manageProjects]);
	
	return (
		<Fragment>
			{privileges.map((privilege) => (
				<Instruction
					key={privilege.id}
					isVisible={tutorials.activeTutorial === privilege.id}
					onProceed={handleViewNextTutorial}
					title={privilege.title}
					message={privilege.message}
				/>
			))}

			<Modal isVisible={createProject} onClose={toggleCreateProject}>
				<CreateProject />
			</Modal>
			
			<Modal isVisible={isNewInspectionVisible} style={{ height: "auto" }} onClose={toggleInspectionVisible}>
				<CreateNewInspectionType />
			</Modal>

			<Modal isVisible={isOverviewVisible} onClose={toggleOverviewVisible} style={{ maxWidth: '60%' }}>
				<ReportOverview isLoading={projectsLoading} projects={projects} />
			</Modal>

			<Modal
				style={{ height: 'auto', maxWidth: '50%' }}
				isVisible={projectToArchive !== null}
				onClose={handleCloseProjectToArchive}
			>
				<div className="p-3">
					<Confirm
						title="Confirm Archive"
						message={`Are you sure you want to archive project: ${projectToArchive?.serialNumber}? This cannot be undone`}
						onCancel={handleCloseProjectToArchive}
						onConfirm={handleArchiveProject}
					/>
				</div>
			</Modal>

			<Header
				onCTAClick={toggleCreateProject}
				subtitle="Manage Projects"
				buttonIcon="file-earmark-plus-fill"
				buttonTitle="Create Project"
				canViewCTA={user.role.manageProjects}
			/>

			<div className={css['projects-body']}>
				<SyncTime lastSynced={projectsLastUpdated} />

				<div className="divider my-4"></div>

				<div className={'container-fluid overflow-hidden ' + css.grid}>
					<div className="row gx-5">
						<Grid
							total={internalDashboard.overview.totalProjectsInDivision}
							label="Total Reports"
							icon="bag-plus"
							loading={internalDashboard.loading}
						/>

						<Grid
							total={internalDashboard.overview.totalInActiveProjectsInDivision}
							label="Active Reports"
							icon="activity"
							loading={internalDashboard.loading}
						/>

						<Grid
							total={internalDashboard.overview.acceptedReports}
							label="Accepted Reports"
							icon="check2-circle"
							loading={internalDashboard.loading}
						/>
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="bg-white p-2 border container-fluid">
					<div className="bg-light p-2 border d-flex justify-content-center align-items-center overflow-auto">
						{renderStatusButtons()}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="d-flex justify-content-end align-items-center">
					<Conditional isVisible={user.id === SUPER_ADMIN_ID}>
						<div className="bg-white p-2 border me-4">
							<Button
								label="Create New Inspection Type"
								icon="plus"
								style={{ margin: 0 }}
								onClick={toggleInspectionVisible}
							/>
						</div>
					</Conditional>

					<div className="bg-white p-2 border me-4">
						<Button
							label="Download"
							icon="download"
							style={{ backgroundColor: '#ab3c3c', margin: 0 }}
							onClick={toggleOverviewVisible}
						/>
					</div>

					<div className={css['projects-date-range']}>
						<DatePickerInput
							date={projectsQuery.startDate ? utils.getDateFromString(projectsQuery.startDate) : new Date()}
							onDateChange={(date: Date) => dispatch(setStartDate(date))}
							placeholderText="Select Start Date"
							maxDate={new Date()}
						/>

						<i className="bi bi-calendar-minus mx-3"></i>

						<DatePickerInput
							date={projectsQuery.endDate ? utils.getDateFromString(projectsQuery.endDate) : new Date()}
							onDateChange={(date: Date) => dispatch(setEndDate(date))}
							placeholderText="Select End Date"
							maxDate={new Date()}
						/>
					</div>

					<Conditional isVisible={!!projectsQuery.startDate || !!projectsQuery.endDate}>
						<div className="mx-4">
							<Button
								label="Clear Date Selections"
								icon="x-circle-fill"
								style={{ marginTop: 0 }}
								onClick={handleClearDateSelections}
							/>
						</div>
					</Conditional>
				</div>

				<div className="divider my-4"></div>

				<div className={css['projects-body-table']}>
					<div className="d-flex justify-content-between mb-2">
						<Elevation isElevated={isActive}>
							<div className="d-flex justify-content-end">
								<div className={css['projects-count']}>
									<i className="bi bi-collection"></i>
									<span>{totalProjects} Available Reports</span>
								</div>

								<Select
									name="page-size"
									placeholder="All Inspection Type(s)"
									options={types}
									value={projectsQuery.inspectionTypeId}
									onChange={handleInspectionType}
								/>

								<div className="mx-3">
									<Filters
										filters={projectsQuery.searchFields ?? []}
										filterOptions={searchOptions}
										onFilterChange={handleFilterChange}
									/>
								</div>
							</div>
						</Elevation>

						<div className="d-flex justify-content-center">
							<Conditional isVisible={user.role.approveReports}>
								<div className={css['projects-refresh']}>
									<Button
										label="Import Old Report"
										icon="upload"
										style={{ backgroundColor: '#000000' }}
										onClick={() => navigate(routes.reportUpload.fullPath)}
									/>
								</div>
							</Conditional>

							<div className={css['projects-refresh']}>
								<Button
									onClick={refreshProjects}
									label="Refresh"
									icon="arrow-clockwise"
								/>
							</div>
						</div>
					</div>

					<div className='table-responsive'>
						<Table
							columns={tableColumns}
							data={projects}
							sortColumn={sortColumn}
							onSort={setSortColumn}
							isFetching={projectsLoading}
						/>
					</div>

					<NoItems
						isVisible={pages.length === 0}
						onRefresh={refreshProjects}
					/>

					<div className="d-flex justify-content-between align-items-center mt-3">
						<Select
							name="page-size"
							placeholder={'Showing (' + projectsQuery.limit + 'rows)'}
							options={utils.transformPageRange(PAGE_SIZE_MAX)}
							value={projectsQuery.limit}
							onChange={handlePageSizeChange}
						/>

						<div className="d-flex justify-content-center gap-3 align-items-center">
							<ItemCount count={dataLength} label='Loaded Projects' />

							{hasNextPage && (
								<Button 
									style={{ marginTop: 0 }}
									icon='arrow-clockwise' 
									label='Load More Projects...'
									onClick={fetchNextPage}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

const columns: models.Column[] = [
	{
		path: 'documentNumber',
		label: 'Document No.',
		content: (project: models.Project) => {
			const user = auth.getCurrentUser();
			
			if (!user) {
				return '';
			}

			if (user.role.previewAllReports && user.id !== project.supervisorId) {
				return <span className={css.documentNumber}>{project.documentNumber}</span>;
			}

			return (
				<Link className={css.documentNumber} to={`/dashboard/projects/${project._id}`}>
					{project.documentNumber}
				</Link>
			)
		},
	},
	{
		path: 'inspectionTypeName',
		label: 'Inspection Type',
	},
	{
		path: 'module',
		label: 'Module',
	},
	{
		path: 'updatedAt',
		label: 'Last Updated',
		content: (item: models.Project) => <span>{utils.formatTime(item.updatedAt)}</span>
	},
	{ path: 'supervisorName', label: 'Supervisor' },
	{
		path: 'organisationName',
		label: 'Organization Name',
	},
	{ 
		path: 'facilityName',
		label: 'Facility',
	},
	{
		path: 'projectStatus',
		label: 'Status',
		content: (item: models.Project) => <ProjectStatusButton isActive status={item.projectStatus} />
	},
];

const searchOptions: models.SelectOption[] = [
	{ id: 'organisationName', name: 'Organization Name' },
	{ id: 'projectTitle', name: 'Project Title' },
	{ id: 'contactPersonName', name: 'Customer Contact Person' },
	{ id: 'facilityName', name: 'Facility' },
	{ id: 'equipmentTag', name: 'Equipment Tag No' },
	{ id: 'equipmentDescription', name: 'Equipment Description' },
	{ id: 'supervisorName', name: 'Supervisor' },
	{ id: 'documentNumber', name: 'Document No' },
	{ id: 'serialNumber', name: 'Report Number' },
	{ id: 'location', name: 'Location' },
	{ id: 'module', name: 'Module' },
	{ id: 'year', name: 'Year' },
	{ id: 'inspectedItemIDs', name: 'Inspected Items' },
];

const initialSortColumn: models.SortColumn = {
	path: 'updatedAt',
	order: 'desc',
};

const allPrivileges: { [key: string]: models.TutorialInstruction } = {
	ALL_PROJECTS: {
		id: tutorialConstants.ALL_PROJECTS,
		next: tutorialConstants.VIEW_PROJECT_COMMENTS,
		title: 'Your Projects are Here!',
		message: 'Created projects will appear here! You can filter by several criteria to access different projects.',
	},
	CREATE_PROJECT: {
		id: tutorialConstants.CREATE_PROJECT,
		next: tutorialConstants.PROFILE_SETTINGS,
		title: 'Create Projects!',
		message: "Click the highlighted 'Create Project' button to get started with a new project!",
	},
};

export default withProjects(withInspectionTypes(Projects));
